/**
 * Web component for Anear counter input (increment/decrement with buttons)
 *
 * Usage:
 *   <anear-counter name="quantity" value="5" min="1" max="20" step="1"></anear-counter>
 *
 * The component maintains local state and includes a hidden input for form integration.
 * Styling is handled via CSS classes on the component and its internal elements.
 */
class AnearCounter extends HTMLElement {
  constructor() {
    super()
    this._value = null
    this._min = null
    this._max = null
    this._step = null
    this._hiddenInput = null
  }

  static get observedAttributes() {
    return ['name', 'value', 'min', 'max', 'step']
  }

  connectedCallback() {
    // Read initial attributes
    const valueAttr = this.getAttribute('value')
    if (valueAttr !== null && this._value === null) {
      this._value = this.parseValue(valueAttr)
    }
    const minAttr = this.getAttribute('min')
    if (minAttr !== null && this._min === null) {
      this._min = this.parseValue(minAttr)
    }
    const maxAttr = this.getAttribute('max')
    if (maxAttr !== null && this._max === null) {
      this._max = this.parseValue(maxAttr)
    }
    const stepAttr = this.getAttribute('step')
    if (stepAttr !== null && this._step === null) {
      this._step = this.parseValue(stepAttr)
    }

    // Create internal structure if it doesn't exist
    if (!this._hiddenInput) {
      this.createInternalStructure()
    }
    this.updateDisplay()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'value' && newValue !== null) {
      this._value = this.parseValue(newValue)
      this.updateValue(this._value, false) // Don't update attribute (would cause loop)
    } else if (name === 'min' && newValue !== null) {
      this._min = this.parseValue(newValue)
      this.updateDisplay()
    } else if (name === 'max' && newValue !== null) {
      this._max = this.parseValue(newValue)
      this.updateDisplay()
    } else if (name === 'step' && newValue !== null) {
      this._step = this.parseValue(newValue)
    } else if (name === 'name' && this._hiddenInput) {
      this._hiddenInput.setAttribute('name', newValue || '')
    }
  }

  parseValue(str) {
    const parsed = parseFloat(str)
    return isNaN(parsed) ? 0 : parsed
  }

  get value() {
    return this._value !== null ? this._value : (this.parseValue(this.getAttribute('value') || '0'))
  }

  set value(newValue) {
    const numValue = this.parseValue(String(newValue))
    this.updateValue(numValue, true)
  }

  get min() {
    return this._min !== null ? this._min : (this.parseValue(this.getAttribute('min') || '0'))
  }

  get max() {
    return this._max !== null ? this._max : (this.parseValue(this.getAttribute('max') || '100'))
  }

  get step() {
    return this._step !== null ? this._step : (this.parseValue(this.getAttribute('step') || '1'))
  }

  createInternalStructure() {
    // Create hidden input for form integration
    this._hiddenInput = document.createElement('input')
    this._hiddenInput.type = 'hidden'
    const nameAttr = this.getAttribute('name')
    if (nameAttr) {
      this._hiddenInput.setAttribute('name', nameAttr)
    }
    this.appendChild(this._hiddenInput)

    // Create container structure
    const container = document.createElement('div')
    container.className = 'anear-counter-container'

    // Decrement button
    const decButton = document.createElement('button')
    decButton.type = 'button'
    decButton.className = 'anear-counter-decrement'
    decButton.innerHTML = '<'
    decButton.addEventListener('click', () => this.decrement())
    container.appendChild(decButton)

    // Value display
    const valueDisplay = document.createElement('span')
    valueDisplay.className = 'anear-counter-value'
    container.appendChild(valueDisplay)

    // Increment button
    const incButton = document.createElement('button')
    incButton.type = 'button'
    incButton.className = 'anear-counter-increment'
    incButton.innerHTML = '>'
    incButton.addEventListener('click', () => this.increment())
    container.appendChild(incButton)

    this.appendChild(container)
  }

  updateValue(newValue, updateAttribute) {
    // Enforce bounds
    const minVal = this.min
    const maxVal = this.max
    const clampedValue = Math.max(minVal, Math.min(maxVal, newValue))

    this._value = clampedValue
    if (this._hiddenInput) {
      this._hiddenInput.value = String(clampedValue)
    }
    if (updateAttribute) {
      this.setAttribute('value', String(clampedValue))
    }
    this.updateDisplay()
  }

  increment() {
    const newValue = this.value + this.step
    this.updateValue(newValue, true)
    // Dispatch change event for potential external listeners
    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      detail: { value: newValue }
    }))
  }

  decrement() {
    const newValue = this.value - this.step
    this.updateValue(newValue, true)
    // Dispatch change event for potential external listeners
    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      detail: { value: newValue }
    }))
  }

  updateDisplay() {
    const container = this.querySelector('.anear-counter-container')
    if (!container) return

    const valueDisplay = container.querySelector('.anear-counter-value')
    const decButton = container.querySelector('.anear-counter-decrement')
    const incButton = container.querySelector('.anear-counter-increment')

    if (valueDisplay) {
      valueDisplay.textContent = String(this.value)
    }

    // Update button disabled states
    if (decButton) {
      decButton.disabled = this.value <= this.min
    }
    if (incButton) {
      incButton.disabled = this.value >= this.max
    }
  }
}

// Register the custom element
if (!customElements.get('anear-counter')) {
  customElements.define('anear-counter', AnearCounter)
}

export default AnearCounter

